import { useState, useEffect } from 'react';
import { VStack, HStack } from '@terra/ui-styled-system';
import { LegacyDialog, Text, Progress, Button } from '@terra/ui-design-system';
import { AlertCircleIcon, Download01Icon } from '@terra/ui-icons';

type ChromiumUpdateState = 'idle' | 'updating' | 'error';
type ChromiumError = {
  code: string;
  message: string;
  canRetry: boolean;
};

export function ChromiumUpdateModal() {
  const [state, setState] = useState<ChromiumUpdateState>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<ChromiumError | null>(null);

  useEffect(() => {
    if (!window.electronAPI?.browser) {
      return;
    }

    const unsubRequired = window.electronAPI.browser.onChromiumUpdateRequired?.(
      () => {
        setState('updating');
        setProgress(0);
        setError(null);
      },
    );
    const unsubProgress =
      window.electronAPI.browser.onChromiumDownloadProgress?.((data) => {
        setProgress(data.percent);
      });
    const unsubComplete = window.electronAPI.browser.onChromiumUpdateComplete?.(
      () => {
        setState('idle');
        setProgress(100);
      },
    );
    const unsubError = window.electronAPI.browser.onChromiumInstallError?.(
      (errorData) => {
        setState('error');
        setError(errorData);
      },
    );

    return () => {
      unsubRequired?.();
      unsubProgress?.();
      unsubComplete?.();
      unsubError?.();
    };
  }, []);
  const handleRetry = () => {
    // Trigger re-check by reloading the app
    window.location.reload();
  };

  if (state === 'idle') {
    return null;
  }

  if (state === 'error') {
    return (
      <LegacyDialog.Root open>
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content p={8} maxW="400px">
            <VStack gap="6" alignItems="flex-start">
              <VStack gap="2" alignItems="flex-start" w="full">
                <HStack gap="2" alignItems="center">
                  <AlertCircleIcon size={20} color="#ef4444" />
                  <LegacyDialog.Title>Installation Failed</LegacyDialog.Title>
                </HStack>
                <Text fontSize="sm" color="fg.muted">
                  {error?.message || 'Unable to download the browser component'}
                </Text>
              </VStack>

              {error?.canRetry && (
                <Button onClick={handleRetry} w="full" size="sm">
                  Retry Download
                </Button>
              )}

              <Text fontSize="xs" color="fg.subtle">
                Browser features unavailable until installation completes
              </Text>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    );
  }

  return (
    <LegacyDialog.Root open>
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content p={8} maxW="400px">
          <VStack gap="6" alignItems="flex-start">
            <VStack gap="2" alignItems="flex-start" w="full">
              <HStack gap="2" alignItems="center">
                <Download01Icon size={20} color="#888" />
                <LegacyDialog.Title>Installing Browser</LegacyDialog.Title>
              </HStack>
              <Text fontSize="sm" color="fg.muted">
                One-time setup for proxy features
              </Text>
            </VStack>

            <Progress value={progress} size="sm" w="full" />
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
